import type { UseApiLoaderResponse } from "../../../../../server/api/hooks/use-api-loader";
import { useApiLoader } from "../../../../../server/api/hooks/use-api-loader";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { channelToFormat } from "../../../../../utils/channel-to-format";
import type { UUID } from "../../../../../utils/uuid";
import type { ChannelName } from "../../../../channels/types";
import { SupabaseEmailDesignService } from "../../../../email-design/server/supabase-email-design-service";
import { useGetPollsWithOptions } from "../../../../poll/hooks/use-poll-with-options";
import {
  isMarkdownDesign,
  type SerialisedEditorDesign,
} from "../../types/serialised-editor-design";
import { createMessageBody, type MessageBody } from "./message-body";

type Config = {
  editorState: SerialisedEditorDesign | null;
  channel: ChannelName;
  account_id: UUID;
  email_design_id?: UUID;
  poll_ids?: UUID[];
};

type MessageBodyCreator = (config: Config) => Promise<
  | {
      data: MessageBody;
      errorMessage?: undefined;
    }
  | { data?: undefined; errorMessage?: string }
>;

export const useMessageBodyCreator = (): UseApiLoaderResponse<MessageBodyCreator> => {
  const getEmailDesign = useSupabaseCallback(
    async ({ supabase, account_id }, email_design_id?: UUID) => {
      const { data: emailDesignData, error } = await new SupabaseEmailDesignService(
        supabase,
      ).maybeGet(email_design_id ?? account_id);

      if (error) {
        throw new Error(`Failed to get email design. ${error?.message ?? ""}`);
      }
      return emailDesignData;
    },
    [],
  );

  const getPolls = useGetPollsWithOptions();

  return useApiLoader(
    // eslint-disable-next-line @typescript-eslint/require-await
    async () => {
      return async ({ editorState, channel, account_id, email_design_id, poll_ids }) => {
        if (!editorState || isMarkdownDesign(editorState)) {
          return { data: { body: null, text: null } };
        }
        if (!channel) return { errorMessage: "Channel not supported" };
        const format = channelToFormat(channel);

        // Only loaded for the channels that use them, as they cost a round trip each.
        const [emailDesign, polls] = await Promise.all([
          format === "email" ? getEmailDesign(email_design_id) : undefined,
          poll_ids ? getPolls(poll_ids) : undefined,
        ]);

        const data = createMessageBody({
          design: editorState,
          channel,
          account_id,
          emailDesign: emailDesign ?? undefined,
          polls,
        });

        return data ? { data } : { errorMessage: "Channel not supported" };
      };
    },
    [getPolls, getEmailDesign],
  );
};
